import { Plus } from "lucide-react";
import type { ReactNode } from "react";
import { Card } from "@flint/ui";

export interface StoreFormSectionProps {
  title: string;
  /** One line on what the section is for — sits under the title. */
  help: string;
  /**
   * Chip beside the heading, for a section whose inputs record nothing. Say
   * *why* — which module owns the data — because a vague "not wired yet" on a
   * section that does save reads as "your input was discarded".
   */
  note?: string;
  children: ReactNode;
}

/**
 * One labelled block of the product form: heading and explanation on top, the
 * fields full-width beneath.
 *
 * Each section is **its own card** (#265, UX-review finding 1): Variants and
 * the Bundle Builder must read as visually separate things, so each section
 * composes `@flint/ui` Card (`as="section"`) and the pages stack them with a
 * gap. The fields get the full width of the card — a side label column costs
 * ~240px that the inputs use better.
 */
export function StoreFormSection({ title, help, note, children }: StoreFormSectionProps) {
  return (
    <Card as="section" className="overflow-hidden px-5 py-5 lg:px-6">
      <div className="mb-4 min-w-0">
        <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
          {title}
          {note ? (
            <span className="rounded-full bg-raised px-2 py-0.5 text-xs font-medium text-muted">
              {note}
            </span>
          ) : null}
        </h2>
        <p className="mt-1 text-xs text-muted">{help}</p>
      </div>
      {/* Single column pinned to minmax(0,1fr): grid items default to
          min-width auto, so a wide child (the variants table) would otherwise
          stretch the track past the card and get clipped unreachably by the
          card's overflow-hidden at narrow widths (#274 review L1). */}
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3.5">{children}</div>
    </Card>
  );
}

/**
 * A row of fields inside a section — two up by default, one when `single`,
 * three when `cols={3}`. Always collapses to one column on narrow screens.
 */
export function StoreFormRow({
  single,
  cols,
  children,
}: {
  single?: boolean;
  cols?: 2 | 3;
  children: ReactNode;
}) {
  const layout = single
    ? "grid gap-4"
    : cols === 3
      ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      : "grid gap-4 sm:grid-cols-2";
  return <div className={layout}>{children}</div>;
}

/** Small "add a value" link under a picker whose options come from the catalog. */
export function AddOptionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"
    >
      <Plus className="size-3" aria-hidden />
      {label}
    </button>
  );
}
